import {
  DeleteFilled,
  FileExclamationFilled,
  FileTextOutlined,
  LinkOutlined,
  LoadingOutlined,
  MailFilled,
  SendOutlined,
} from '@ant-design/icons'
import { useDriverSchedules } from '@api/useDriverSchedules'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { useOrderDispatchRowPinning } from '@components/OrderDispatch/OrderDispatch'
import { OrderRequestType } from '@components/OrderDispatch/OrderGrid/OrderColumn'
import { getExtraOrderAttributes } from '@components/OrderDispatch/OrderGrid/OrderRow'
import { ExplicitView } from '@components/OrderDispatch/TripGrid/TripGridCell/ExplicitView/ExplicitView'
import { OrderTerminals } from '@components/_SiteWide/OrderDisplay/OrderTerminals'
import { dateFormat } from '@constants/dateFormat'
import { ItemTypes, dropActions, statusClassDict } from '@constants/orderDispatch'
import { GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Popover, Tooltip, theme } from 'antd'
import classNames from 'classnames'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { useLockedSchedules } from '@hooks/useLockedSchedules'
import { FlagIcon, TransportationOrderFlag } from '../../../_SiteWide/Flags/FlagIcon'
import { OrderProducts } from '../../../_SiteWide/OrderDisplay/OrderProducts'
import { StoreCityStateList } from '../../../_SiteWide/OrderDisplay/StoreCityStateList'
import { useAcceptedOrdersContext } from '../../Contexts/AcceptedOrdersContext'
import { useScheduleContext } from '../../Contexts/ScheduleContext'
import { DispatchWindowPopover } from './DispatchWindowPopover'
import { LockButton } from './LockButton'
import { TripStatusHover } from './TripStatus'
import { getTripErrors, shouldShowLink } from './cardStatus'

export function TripGridCell({
  triggerModal,
  trip,
  scheduleIndex,
  tripIndex,
  schedule,
  triggerLinkWizard,
  setOrderHistoryStoreId,
  tripTimeDisplay,
  config,
  sendCarrierOrderMutation,
  ordersGridRef,
}) {
  const { token } = theme.useToken()
  const { rowPinningApiRef } = useOrderDispatchRowPinning()
  const [flags, setFlags] = useState(trip.flags)
  const { scopes } = useSecurityContext()
  const { addOrderToTrip, moveOrder, lockTrip, unlockTrip, loadingTripCard, setLoadingTripCard } = useScheduleContext()
  const { removeOrder, isDraggingActionBlock, isRowDragEnabled } = useAcceptedOrdersContext()

  const loading = useMemo(
    () => loadingTripCard?.driver_schedule_id === schedule.id && loadingTripCard?.slot_index === tripIndex,
    [loadingTripCard, schedule, tripIndex]
  )

  const setLoading = useCallback(
    (flag) => {
      if (flag) {
        setLoadingTripCard({
          driver_schedule_id: schedule.id,
          slot_index: tripIndex,
        })
      } else {
        setLoadingTripCard(null)
      }
    },
    [schedule, tripIndex]
  )

  const { lockedSchedules } = useLockedSchedules()
  const optimizing = useMemo(
    () => lockedSchedules?.map((s) => s.id).includes(schedule.id),
    [lockedSchedules, schedule.id]
  )
  const isBlocked = useMemo(() => {
    if (!schedule.invalid_credentials) return false
    return schedule.invalid_credentials?.some(
      (cred) => cred.dispatch_impact === 'Block Dispatch' && cred.status === 'Expired' && cred.is_blocked
    )
  }, [schedule.invalid_credentials])
  const status = useMemo(() => trip.trip_status, [trip?.trip_status])
  const canMoveOrder = useMemo(
    () => !optimizing && scopes.order_dispatch?.edit,
    [optimizing, scopes?.order_dispatch?.edit]
  )

  const isLocked = useMemo(() => {
    return trip.move_disabled || trip.locked || trip.is_linked || optimizing || isBlocked
  }, [trip, optimizing, trip?.locked, trip?.move_disabled, trip?.is_linked, isBlocked])

  const showOrderFlags = useMemo(() => trip?.flags && trip.flags.length > 0, [trip?.flags])

  useEffect(() => {
    if (trip?.flags?.length > 0) setFlags(trip.flags)
  }, [trip])

  const hoverTriggerItem = useMemo(() => {
    if (trip.flags.some((flag) => flag.type === 'compliance_hold')) {
      return <FileExclamationFilled appearance='text' />
    }
    return null
  }, [trip?.flags])

  const currentStatusClass = useMemo(() => statusClassDict[status], [status])

  const handleFlagResolve = (order) => {
    setFlags(order.flags.filter((flag) => flag.type !== 'dependent'))
  }

  // linkedBlock prevents an order from being added if the linked order is the last order on a driver's schedule
  const linkedBlocker = !!(schedule.trips?.[tripIndex - 1]?.is_linked && tripIndex !== 1)

  const advancedLinkBlocker = () => {
    let i = tripIndex - 1
    while (i > 1) {
      i--
      if (schedule.trips?.[i]?.is_linked) return true
    }
  }

  const handleCarrierSend = useCallback(() => {
    sendCarrierOrderMutation.mutateAsync({
      schedule_id: schedule.id,
      order_ids: [trip.order_id],
      force: false,
      instant: true,
    })
  }, [schedule.id, trip.order_id, sendCarrierOrderMutation])

  const [, drag] = useDrag({
    item: {
      setLoading,
      type: ItemTypes.TRIPGRIDCELL,
      scheduleIndex,
      tripIndex,
      order: trip,
      scheduleId: schedule.id,
      version: schedule.version,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: () => canMoveOrder && !isLocked,
  })
  function isOrderAssignable(order) {
    const attrs = getExtraOrderAttributes(order, order.lockedOrders, scopes)
    return !attrs.lockedOrder && order.type !== OrderRequestType.OrderRequestRequested && !optimizing && !isBlocked
  }
  const dropRef = useRef(null)

  // Dirty hack to make sure dropZoneParams always knows the value of isRowDragEnabled
  // without needing to re-compute.
  const isRowDragEnabledRef = useRef(isRowDragEnabled)

  useEffect(() => {
    isRowDragEnabledRef.current = isRowDragEnabled
  }, [isRowDragEnabled])

  const dropZoneParams = useMemo(() => {
    const params = {
      getContainer: () => dropRef?.current,
      onDragStop: (e) => {
        if (
          isOrderAssignable(e?.node?.data) &&
          !e.node.group &&
          !isLocked &&
          !linkedBlocker &&
          !advancedLinkBlocker()
        ) {
          handleOrderGridDrop(e)
          setIsOrderRowOver(false)
        }
      },
      onDragEnter: (e) => {
        if (
          isOrderAssignable(e?.node?.data) &&
          !e.node.group &&
          !isLocked &&
          !linkedBlocker &&
          !advancedLinkBlocker()
        ) {
          setIsOrderRowOver(true)
          dropRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
        }
      },
      onDragLeave: (e) => {
        if (isOrderAssignable(e?.node?.data) && !e.node.group && !linkedBlocker && !advancedLinkBlocker()) {
          setIsOrderRowOver(false)
        }
      },
    }
    return params
  }, [
    ordersGridRef,
    dropRef.current,
    schedule?.id,
    isRowDragEnabledRef.current,
    isLocked,
    linkedBlocker,
    advancedLinkBlocker,
  ])

  useEffect(() => {
    if (ordersGridRef?.current && dropRef.current) {
      const gridApi = ordersGridRef.current
      gridApi.removeRowDropZone(dropZoneParams)
      gridApi.addRowDropZone(dropZoneParams)
    }
  }, [ordersGridRef, dropRef.current, dropZoneParams])

  useEffect(() => {
    return () => {
      if (ordersGridRef?.current) {
        const gridApi = ordersGridRef?.current
        gridApi.removeRowDropZone(dropZoneParams)
      }
    }
  }, [])

  function handleDrop(src) {
    const action = trip.order_id ? dropActions.INSERT : dropActions.MOVE
    setLoading(true)
    const onComplete = () => setLoading(false)
    const tgt = { scheduleId: schedule.id, tripIndex, version: schedule.version }

    const request = {
      src_driver_schedule_id: src.scheduleId,
      src_version: src.version,
      src_slot_index: src.tripIndex,
      tgt_driver_schedule_id: tgt.scheduleId,
      tgt_version: tgt.version,
      tgt_slot_index: tgt.tripIndex,
      action,
    }

    moveOrder(request, onComplete, action)
  }

  const [isOrderRowOver, setIsOrderRowOver] = useState(false)

  const [{ isOver }, drop] = useDrop({
    accept: [ItemTypes.ORDERROW, ItemTypes.TRIPGRIDCELL],
    drop: handleDrop,
    collect: (mon) => ({
      isOver: !!mon.isOver(),
      canDrop: canMoveOrder && !!mon.canDrop() && !advancedLinkBlocker(),
    }),
    canDrop: (_item, _monitor) => {
      return !optimizing && canMoveOrder && !isLocked && !linkedBlocker && !isBlocked
    },
  })

  const toggleLock = () => {
    setLoading(true)
    const onComplete = () => setLoading(false)

    if (canMoveOrder && trip.locked) {
      unlockTrip(schedule.id, tripIndex, onComplete)
    } else {
      lockTrip(schedule.id, tripIndex, onComplete)
    }
  }

  async function handleOrderGridDrop(e) {
    const data = e.node.data
    setLoading(true)
    const request = {
      order_id: data.id,
      driver_schedule_id: schedule.id,
      slot_index: tripIndex,
      version: schedule.version,
      action: trip.order_id ? dropActions.INSERT : dropActions.MOVE,
    }
    const assigned = await addOrderToTrip(request, () => setLoading(false), data.flags)

    if (assigned) {
      rowPinningApiRef?.current?.unpinRow(data.number)
      removeOrder(data.id)
    }
  }

  const canDrop =
    isOver && !optimizing && canMoveOrder && !isLocked && !linkedBlocker && !advancedLinkBlocker() && !isBlocked

  if (loading) {
    return (
      <div
        className='trip-cell'
        style={{
          backgroundColor: token.colorInfoBg,
          alignItems: 'center',
          justifyContent: 'center',
          border: `solid 2px ${token.colorInfo}`,
          borderRadius: '6px',
        }}
      >
        <LoadingOutlined style={{ fontSize: 24 }} color={token.colorInfo} />
      </div>
    )
  }

  const hideClass =
    trip.trip_status === 'complete' ||
    trip.trip_status === 'loading' ||
    trip.trip_status === 'driving to drop' ||
    trip.trip_status === 'arrived at drop' ||
    trip.trip_status === 'dropping'

  if (trip && trip.order_id) {
    const cardErrors = getTripErrors(trip, config, schedule)
    const firstError = cardErrors[0]
    const lockDisabled = trip.move_disabled || trip.is_linked
    const lockedStatus = isLocked ? 'locked' : ''
    const isBackhaul = trip.type?.toLowerCase() === 'backhaul'

    const isWarningTrip = !hideClass && firstError.status === 'warning' && trip.state !== 'complete'
    const isErroredTrip =
      !hideClass && (trip.card_alerts?.length > 0 || firstError.status === 'error') && trip.state !== 'complete'
    const getLockText = () => {
      if (optimizing) {
        return 'Trip cannot be moved due to route optimization.'
      }
      if (trip.move_disabled) {
        return 'Trip cannot be moved due to driver progress.'
      }
      if (trip.is_linked) {
        return 'Unlink this to move/edit the order.'
      }
      if (isLocked) {
        return 'Unlock Order'
      }
      return 'Lock Order'
    }

    return (
      <div
        className={classNames({
          'vertical-flex': true,
          'h-100': true,
          overlay: true,
          'isover-container': canDrop,
          'optimizing-container': optimizing || isBlocked,
        })}
        ref={(instance) => {
          drop(instance)
          dropRef.current = instance
        }}
      >
        <div
          ref={optimizing ? null : drag}
          data-cy='order-card'
          style={{ maxWidth: 330 }}
          className={classNames(
            'trip-cell',
            'scheduled-cell',
            { 'move-cursor': canMoveOrder },
            { 'replace-card': canDrop || isOrderRowOver },
            { 'linked-order': trip.is_linked },
            { 'trip-warning': isWarningTrip },
            { 'trip-error': isErroredTrip },
            currentStatusClass
          )}
        >
          <div style={{ borderRadius: '6px 0 0 6px' }} className={`trip-card-buttons ${currentStatusClass}`}>
            {!schedule.carrier_id && (
              <>
                <LinkButton
                  schedule={schedule}
                  tripIndex={tripIndex}
                  triggerLinkWizard={triggerLinkWizard}
                  trip={trip}
                  disabled={optimizing}
                  isError={isErroredTrip || isWarningTrip}
                />
              </>
            )}
            <Vertical justifyContent='center' flex={1}>
              <Tooltip title={`Order ${trip.number} Details`}>
                <GraviButton
                  appearance='text'
                  size='small'
                  data-cy='order-modal-btn'
                  icon={<FileTextOutlined />}
                  onClick={() => triggerModal(trip.order_id)}
                />
              </Tooltip>
            </Vertical>
            <Vertical justifyContent='center' flex={1}>
              <Tooltip title={getLockText()} appearance='text'>
                <div>
                  <LockButton
                    className={classNames(
                      'trip-lock-button',
                      {
                        'super-locked': lockDisabled,
                      },
                      currentStatusClass
                    )}
                    classNameLocked='trip-locked'
                    classNameUnlocked='trip-unlocked'
                    isLocked={isLocked}
                    onClick={toggleLock}
                    disabled={optimizing}
                    appearance='text'
                  />
                </div>
              </Tooltip>
            </Vertical>
            {schedule.carrier_id && (
              <GraviButton
                theme1
                icon={
                  trip.carrier_notify_state === 'sent' ? (
                    <MailFilled style={{ fontSize: 13 }} appearance='text' />
                  ) : (
                    <SendOutlined style={{ fontSize: 12 }} appearance='text' />
                  )
                }
                className='view-details'
                onClick={handleCarrierSend}
                disabled={['pending', 'sent'].includes(trip.carrier_notify_state)}
              />
            )}
          </div>
          {tripTimeDisplay === 'explicit' ? (
            <ExplicitView
              trip={trip}
              currentStatusClass={currentStatusClass}
              status={status}
              cardErrors={cardErrors}
              hideClass={hideClass}
              isBackhaul={isBackhaul}
              setOrderHistoryStoreId={setOrderHistoryStoreId}
              flags={flags}
              showFlags={showOrderFlags}
              handleFlagResolve={handleFlagResolve}
            />
          ) : (
            <Horizontal
              flex={1}
              border='all'
              borderRadius='0 6px 6px 0'
              className={`card-body flex-div flex-1 p-2  ${currentStatusClass}`}
            >
              <div
                className={`trip-card-details-container  flex-1 vertical-flex-center ${lockedStatus} ${
                  status === 'complete' ? 'trip-complete' : ''
                }`}
              >
                <div className=' flex-1 flex items-center'>
                  <StoreCityStateList order={trip} setOrderHistoryStoreId={setOrderHistoryStoreId} />
                </div>
                <div className=' flex-1 flex items-center'>
                  <OrderTerminals order={trip} showShortName />
                </div>
                <div className='flex-1 flex items-center'>
                  <OrderProducts order={trip} />
                </div>
              </div>
              <Vertical flex='0' style={{ overflow: 'visible', alignItems: 'flex-end' }}>
                <div className='flex-1'>
                  {status !== 'complete' && (
                    <DispatchWindowPopover
                      order={trip}
                      cardErrors={cardErrors}
                      hideClass={hideClass}
                      isBackhaul={isBackhaul}
                      hoverTriggerItem={hoverTriggerItem}
                    />
                  )}
                </div>
                <div className='flex-1 flex items-center'>
                  {isBackhaul ? (
                    <TransportationOrderFlag />
                  ) : (
                    <>
                      {showOrderFlags && (
                        <FlagIcon
                          flags={flags}
                          orderId={trip.order_id}
                          orderNumber={trip.number}
                          updateOrder={handleFlagResolve}
                          view='tripGridCell'
                          appearance='text'
                          useButtonStyle={true}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className='flex-1 justify-end flex items-center'>
                  <TripStatusHover
                    appearance='text'
                    status={status}
                    trip={trip}
                    currentStatusClass={currentStatusClass}
                  />
                </div>
              </Vertical>
            </Horizontal>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        ref={(instance) => {
          drop(instance)
          dropRef.current = instance
        }}
        className={`trip-cell ${isOver ? 'no-order-isover-container' : ''} ${
          optimizing || isBlocked ? 'no-order-optimizing-container' : ''
        }`}
      />
      <div
        className='empty'
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: isOrderRowOver && !isDraggingActionBlock ? token.colorSuccessBgHover : 'transparent',
          border: isOrderRowOver && !isDraggingActionBlock ? `dotted 2px ${token.colorSuccessBorder}` : 'none',
          opacity: 0.5,
        }}
        onDragEnter={(e) => {
          setIsOrderRowOver(true)
          dropRef.current.dispatchEvent(new DragEvent(e.type, e))
        }}
        onDragLeave={(e) => {
          setIsOrderRowOver(false)
          dropRef.current.dispatchEvent(new DragEvent(e.type, e))
        }}
        onDrop={(e) => {
          setIsOrderRowOver(false)
          dropRef.current.dispatchEvent(new DragEvent(e.type, e))
        }}
      />
    </>
  )
}

function LinkButton({ trip, triggerLinkWizard, schedule, tripIndex, disabled }) {
  const { updateTripGrid, setSchedules } = useScheduleContext()

  if (disabled) return null
  if (trip.is_linked) {
    return (
      <Vertical justifyContent='center' flex={1}>
        <Popover
          content={
            <ScheduleLink trip={trip} setSchedules={setSchedules} updateTripGrid={updateTripGrid} schedule={schedule} />
          }
          title='Linked Order'
          trigger='hover'
        >
          <GraviButton size='small' appearance='solid' shape='circle' theme1 icon={<LinkOutlined />} />
        </Popover>
      </Vertical>
    )
  }
  if (shouldShowLink(trip, schedule, tripIndex)) {
    return (
      <Vertical justifyContent='center' flex={1}>
        <Tooltip title='Link Order'>
          <GraviButton
            onClick={() => triggerLinkWizard(schedule, tripIndex)}
            theme2={trip.is_linked}
            icon={<LinkOutlined />}
            appearance={trip.is_linked ? 'solid' : 'text'}
            size='small'
          />
        </Tooltip>
      </Vertical>
    )
  }
  return null
}
function ScheduleLink({ trip, setSchedules, updateTripGrid, schedule }) {
  const [otherSchedule, setOtherSchedule] = useState(null)
  const { linkToDriver, handleUnlink } = useScheduleContext()

  const linkPayload = { order_id: trip.order_id, driver_schedule_id: schedule.id }
  const { unlinkOrderMutation, getLinkedScheduleQuery } = useDriverSchedules()
  const { data: linkedScheduleData } = getLinkedScheduleQuery(linkPayload)

  useEffect(() => {
    if (linkedScheduleData) setOtherSchedule(linkedScheduleData)
  }, [linkedScheduleData])

  if (otherSchedule) {
    return (
      <>
        <div>
          This order is currently linked to {otherSchedule.driver_name}'s schedule{' '}
          {formatters.DATE(moment(otherSchedule.date))} - {otherSchedule.shift.toUpperCase()}.
        </div>
        <div className='flex items-center justify-center'>
          <GraviButton
            error
            className='mt-3 flex-1 mr-3'
            buttonText='Remove Link'
            icon={<DeleteFilled />}
            appearance='text'
            onClick={() => handleUnlink({ order_id: trip.order_id })}
          />
          <GraviButton
            className='mt-3 flex-1 '
            buttonText='View Linked Schedule'
            icon={<LinkOutlined />}
            appearance='text'
            onClick={() =>
              linkToDriver(
                otherSchedule.driver_name,
                moment(otherSchedule.date).format(dateFormat.YEAR_MONTH_DATE), // TODO: payload format
                otherSchedule.shift
              )
            }
          />
        </div>
      </>
    )
  }
  return null
}
